/**
 * --- KONSEP: LAYANAN PENJUALAN (SALES SERVICE) ---
 * File ini mengelola semua data transaksi penjualan, 
 * mulai dari mencatat nota baru hingga melihat riwayat struk.
 */
import { supabase } from '../lib/supabase';

export const salesService = {
  /**
   * --- LOGIKA: Cari Nota Penjualan ---
   * Mencari nota berdasarkan Nomor Transaksi atau Nama Pelanggan.
   */
  async searchSales(searchQuery, limit = 3) {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    let query = supabase
      .from("sales")
      .select("id, transaction_id, customer_name");

    if (userId) {
      query = query.eq("sales_person_id", userId);
    }

    const { data, error } = await query
      .or(`transaction_id.ilike.%${searchQuery}%,customer_name.ilike.%${searchQuery}%`)
      .limit(limit);
      
    if (error) throw error;
    return data || [];
  },

  /**
   * --- LOGIKA: Ambil Riwayat Penjualan ---
   * Mengambil semua daftar transaksi yang pernah terjadi, 
   * diurutkan dari yang paling baru.
   */
  async getSalesHistory() {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    let query = supabase
      .from("sales")
      .select("*");

    if (userId) {
      query = query.eq("sales_person_id", userId);
    }

    const { data, error } = await query.order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  },

  /**
   * --- LOGIKA: Ambil Data Struk Lengkap ---
   * Mengambil detail satu nota beserta daftar barang yang dibeli di nota tersebut.
   */
  async getReceiptData(receiptId) {
    // 1. Ambil Ringkasan Nota (Total, Nama Pembeli, dll)
    const { data: sale, error: saleError } = await supabase
      .from("sales")
      .select("*")
      .eq("id", receiptId)
      .single();
    if (saleError) throw saleError;

    // 2. Ambil Rincian Barang yang dibeli di nota tersebut
    const { data: items, error: itemsError } = await supabase
      .from("sale_items")
      .select(`*, products:product_id(name, sku)`)
      .eq("sale_id", receiptId);
    if (itemsError) throw itemsError;

    return { sale, items: items || [] };
  },

  /**
   * --- LOGIKA: Simpan Transaksi Baru ---
   * Mencatat penjualan baru ke database dan merinci barang-barangnya.
   */
  async createSaleRecord(salePayload, itemsPayload) {
    // 1. Simpan Ringkasan Penjualannya dulu
    const { data: saleData, error: saleError } = await supabase
      .from("sales")
      .insert([salePayload])
      .select()
      .single();
    if (saleError) throw saleError;

    // 2. Simpan semua barang yang ada di keranjang ke tabel rincian
    const itemsWithSaleId = itemsPayload.map(item => ({...item, sale_id: saleData.id}));
    const { error: itemsError } = await supabase
      .from("sale_items")
      .insert(itemsWithSaleId);
    if (itemsError) throw itemsError;

    return saleData;
  }
};
